import React from 'react';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { PerplexityContent } from '../types/PerplexityContent';
import { ResearchTypeDisplaySection } from './ResearchTypeDisplaySection';
import { TimeframeDisplaySection } from './TimeframeDisplaySection';
import { TypeSpecificParametersSection } from './TypeSpecificParametersSection';
import { ParametersListSection } from './ParametersListSection';
import { RelatedQuestionsSection } from './RelatedQuestionsSection';

interface PerplexityParametersSectionProps {
  content: PerplexityContent;
  onRelatedQuestionClick?: (question: string) => void;
  isGeneratingRelatedQuestion?: boolean;
  onGenerationStateChange?: (isGenerating: boolean) => void;
  contentType?: 'research' | 'private_research'; // NEW: Optional prop for content type
  showRelatedQuestions?: boolean; // NEW: Optional prop to hide related questions section
}

export const PerplexityParametersSection: React.FC<PerplexityParametersSectionProps> = ({
  content,
  onRelatedQuestionClick,
  isGeneratingRelatedQuestion = false,
  onGenerationStateChange,
  contentType = 'research', // Default to 'research' for backward compatibility
  showRelatedQuestions = true // Default to true for backward compatibility
}) => {
  const { metadata } = content;
  
  // Extract parameters from metadata
  const parameters = {
    research_type: metadata?.researchType || content.contentType,
    timeframe: metadata?.timeframe,
    timeframe_label: metadata?.timeframe,
    date_range_start: metadata?.parameters?.date_range_start,
    date_range_end: metadata?.parameters?.date_range_end,
    location: metadata?.location,
    country: metadata?.parameters?.country,
    type_specific_filters: metadata?.parameters?.type_specific_filters || {},
    // Remove favorites and other metadata that shouldn't appear as parameters
    ...Object.fromEntries(
      Object.entries(metadata?.parameters || {}).filter(([key]) => 
        !['isFavorited', 'favoriteCount', 'user_id', 'created_at'].includes(key)
      )
    )
  };

  // Check if we have any content to display
  const hasParameters = Object.values(parameters).some(value => 
    value !== null && value !== undefined && value !== ''
  );
  const hasRelatedQuestions = metadata?.relatedQuestions && metadata.relatedQuestions.length > 0;
  const hasAnyContent = hasParameters || hasRelatedQuestions;

  // Loading state
  if (!content || !metadata) {
    return (
      <div className="p-3 space-y-3">
        <div>
          <Skeleton className="h-5 w-32 mb-3" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        </div>
        <div>
          <Skeleton className="h-5 w-40 mb-3" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!hasAnyContent) {
    return (
      <div className="p-3 text-center text-muted-foreground text-sm">
        No additional parameters available
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <TooltipProvider>
        {/* Related Questions Section - Show first for better UX (conditionally) */}
        {showRelatedQuestions && (
          <RelatedQuestionsSection 
            relatedQuestions={metadata?.relatedQuestions}
            onRelatedQuestionClick={onRelatedQuestionClick}
            currentResearchId={content.id}
            currentResearchParameters={parameters}
            onGenerationStateChange={onGenerationStateChange}
            contentType={contentType}
          />
        )}
        
        {/* Research Type Display */}
        <ResearchTypeDisplaySection 
          researchType={parameters.research_type} 
        />
        
        {/* Timeframe Display with Color Coding */}
        <TimeframeDisplaySection 
          parameters={parameters} 
        />
        
        {/* Type-Specific Parameters */}
        <TypeSpecificParametersSection 
          parameters={parameters} 
        />
        
        {/* General Parameters List */}
        <ParametersListSection 
          parameters={parameters} 
        />

        {/* Sources Count Display */}
        {content.sources && content.sources.length > 0 && (
          <div className="flex items-center gap-3 p-3 bg-background/50 dark:bg-background/80 rounded-2xl border">
            <div className="text-muted-foreground">📚</div>
            <div className="flex-1">
              <div className="font-medium text-sm text-foreground">Sources</div>
              <div className="text-sm text-muted-foreground">
                {content.sources.length} source{content.sources.length !== 1 ? 's' : ''} referenced
              </div>
            </div>
          </div>
        )}


      </TooltipProvider>
    </div>
  );
}; 